import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { TagawattDataset } from './core/_models'
import {
  getCardIdForSeries,
  getSeriesKey,
  isCardSeriesVisible,
  sumDatasetDailyTotal,
} from './liveDataSeriesUtils'

export type LiveDataSummaryCardsProps = {
  datasets: TagawattDataset[]
  chartType: string
  resolution: '30min' | '1min'
  isIntradayView: boolean
  isTodayView: boolean
  isLoading: boolean
  colorByName: Record<string, string>
  hiddenSeriesKeys: Set<string>
  onToggleCard: (cardId: string) => void
}

export type SummaryCardItem = {
  id: string
  title: string
  subtitle?: string
  value: number
  unit: string
  accentColor: string
  comparePercent?: number | null
  compareLabel?: string
  seriesKeys: string[]
}

const READINGS_CARD_COLORS = ['#008FFB', '#00E396']

export function buildLiveDataSummaryCards(
  datasets: TagawattDataset[],
  resolution: '30min' | '1min',
  isIntradayView: boolean,
  isTodayView: boolean,
  chartType: string,
  colorByName: Record<string, string>,
  intl: ReturnType<typeof useIntl>
): SummaryCardItem[] {
  if (!datasets.length) return []

  const cards: SummaryCardItem[] = []
  const tagDatasets = datasets.filter((ds) => ds.source === 'tagawatt' || ds.sensor_code)
  const readingsDatasets = datasets.filter((ds) => ds.source === 'readings')

  const periodLabel = intl.formatMessage({ id: chartType, defaultMessage: chartType })

  if (readingsDatasets.length > 0 && !(isIntradayView && resolution === '1min')) {
    const thisYearDs = readingsDatasets[0]
    const lastYearDs = readingsDatasets[1]
    const thisYearTotal = sumDatasetDailyTotal(thisYearDs, isTodayView)
    const lastYearTotal = lastYearDs ? sumDatasetDailyTotal(lastYearDs, isTodayView) : 0

    let comparePercent: number | null = null
    if (lastYearDs && lastYearTotal > 0 && thisYearTotal > 0) {
      comparePercent = Math.round(((thisYearTotal - lastYearTotal) / lastYearTotal) * 100)
    }

    cards.push({
      id: getCardIdForSeries(getSeriesKey(thisYearDs)),
      title: intl.formatMessage({ id: 'BENCHMARK.THIS_YEAR' }),
      subtitle: thisYearDs.name || periodLabel,
      value: thisYearTotal,
      unit: 'kWh',
      accentColor: READINGS_CARD_COLORS[0],
      comparePercent,
      compareLabel: lastYearDs ? intl.formatMessage({ id: 'TAGAWATT.VS_LAST_YEAR' }) : undefined,
      seriesKeys: [getSeriesKey(thisYearDs)],
    })

    if (lastYearDs) {
      cards.push({
        id: getCardIdForSeries(getSeriesKey(lastYearDs)),
        title: intl.formatMessage({ id: 'BENCHMARK.LAST_YEAR' }),
        subtitle: lastYearDs.name || periodLabel,
        value: lastYearTotal,
        unit: 'kWh',
        accentColor: READINGS_CARD_COLORS[1],
        seriesKeys: [getSeriesKey(lastYearDs)],
      })
    }
  }

  tagDatasets.forEach((ds) => {
    const seriesKey = getSeriesKey(ds)
    cards.push({
      id: getCardIdForSeries(seriesKey),
      title: ds.name,
      subtitle: periodLabel,
      value: sumDatasetDailyTotal(ds, isTodayView),
      unit: 'kWh',
      accentColor:
        (ds.sensor_code && colorByName[ds.sensor_code]) || colorByName[ds.name] || '#00BCD4',
      seriesKeys: [seriesKey],
    })
  })

  return cards
}

function formatValue(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: value >= 100 ? 0 : 1,
    maximumFractionDigits: value >= 100 ? 0 : 2,
  })
}

const LiveDataSummaryCards: React.FC<LiveDataSummaryCardsProps> = ({
  datasets,
  chartType,
  resolution,
  isIntradayView,
  isTodayView,
  isLoading,
  colorByName,
  hiddenSeriesKeys,
  onToggleCard,
}) => {
  const intl = useIntl()

  const cards = useMemo(
    () =>
      buildLiveDataSummaryCards(
        datasets,
        resolution,
        isIntradayView,
        isTodayView,
        chartType,
        colorByName,
        intl
      ),
    [datasets, resolution, isIntradayView, isTodayView, chartType, colorByName, intl]
  )

  if (isLoading) {
    return (
      <div className='row g-4 g-xl-5 mb-5'>
        {[1, 2, 3, 4].map((key) => (
          <div className='col-6 col-md-4 col-xl-3' key={key}>
            <div className='card border-0 h-100 placeholder-glow'>
              <div className='card-body py-5'>
                <span className='placeholder col-7 mb-3'></span>
                <span className='placeholder col-10 fs-2'></span>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (cards.length === 0) return null

  return (
    <div className='row g-4 g-xl-5 mb-5'>
      {cards.map((card) => {
        const isVisible = isCardSeriesVisible(card.id, hiddenSeriesKeys)
        const isDown = card.comparePercent != null && card.comparePercent < 0
        const isUp = card.comparePercent != null && card.comparePercent > 0
        const compareColor = isDown ? '#09b585' : isUp ? '#f1416c' : '#a1a5b7'

        return (
          <div className='col-6 col-md-4 col-xl-3' key={card.id}>
            <div
              role='button'
              tabIndex={0}
              className='card border-0 h-100 overflow-hidden position-relative'
              style={{
                background: isVisible
                  ? `linear-gradient(145deg, #ffffff 35%, ${card.accentColor}14 100%)`
                  : 'linear-gradient(145deg, #f9fafb 35%, #eef1f5 100%)',
                boxShadow: isVisible
                  ? '0 8px 24px rgba(15, 23, 42, 0.06)'
                  : 'inset 0 0 0 1px rgba(148, 163, 184, 0.35)',
                opacity: isVisible ? 1 : 0.72,
                cursor: 'pointer',
                transition: 'opacity 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
              }}
              onClick={() => onToggleCard(card.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onToggleCard(card.id)
                }
              }}
            >
              <button
                type='button'
                className='btn btn-icon btn-sm btn-light position-absolute top-0 end-0 m-2'
                style={{ zIndex: 1, width: 32, height: 32 }}
                aria-label={
                  isVisible
                    ? intl.formatMessage({ id: 'TAGAWATT.HIDE_SERIES' })
                    : intl.formatMessage({ id: 'TAGAWATT.SHOW_SERIES' })
                }
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleCard(card.id)
                }}
              >
                <i className={`fa ${isVisible ? 'fa-eye' : 'fa-eye-slash'} fs-6`} />
              </button>

              <div className='card-body d-flex flex-column py-5 px-5 pe-12'>
                <div className='d-flex align-items-center gap-2 mb-3'>
                  <span
                    className='rounded-circle flex-shrink-0'
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: isVisible ? card.accentColor : '#94a3b8',
                    }}
                  />
                  <span
                    className={`fs-7 fw-bold text-truncate ${
                      isVisible ? 'text-gray-600' : 'text-gray-500'
                    }`}
                  >
                    {card.title}
                  </span>
                </div>

                <div className='d-flex align-items-baseline gap-2 mb-1'>
                  <span
                    className={`fs-2x fw-bold ${isVisible ? 'text-gray-900' : 'text-gray-500'}`}
                  >
                    {formatValue(card.value)}
                  </span>
                  <span className='fs-6 fw-semibold text-gray-500'>{card.unit}</span>
                </div>

                {card.subtitle ? (
                  <span className='fs-8 text-gray-500 text-truncate'>{card.subtitle}</span>
                ) : null}

                {!isVisible ? (
                  <span className='fs-8 text-gray-500 mt-2'>
                    {intl.formatMessage({ id: 'TAGAWATT.HIDDEN_ON_CHART' })}
                  </span>
                ) : null}

                {isVisible && card.comparePercent != null && card.compareLabel ? (
                  <div className='mt-3 d-flex align-items-center gap-2'>
                    <span className='fs-7 fw-bold' style={{ color: compareColor }}>
                      {isUp ? '↑' : isDown ? '↓' : '•'} {Math.abs(card.comparePercent)}%
                    </span>
                    <span className='fs-8 text-gray-500'>{card.compareLabel}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default LiveDataSummaryCards
